import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { usePermissionsApi, useRolesApi, useUsersManagementApi } from "src/_common/hooks/actions";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { URLS } from "src/_config";

const PermissionList: React.FC = () => {
  const { t } = useTranslation("member");

  // Dynamic state for modules and actions from API
  const [modulesData, setModulesData] = useState<{ [key: string]: string }>({});
  const [actionsData, setActionsData] = useState<{ [key: string]: string }>({});
  const [availableModuleActions, setAvailableModuleActions] = useState<{ [module: string]: string[] }>({});

  // Helper functions to map between API module names and display names
  const getModuleDisplayName = (moduleKey: string): string => {
    return modulesData[moduleKey] || moduleKey;
  };

  const getModuleKey = (displayName: string): string => {
    // Find module key by display name
    const moduleEntry = Object.entries(modulesData).find(([key, value]) => value === displayName);
    return moduleEntry ? moduleEntry[0] : displayName.toLowerCase();
  };

  const [selectedRole, setSelectedRole] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState(0);
  const [initialRoleId, setInitialRoleId] = useState(0); // Track initial role from API
  const [userRoleId, setUserRoleId] = useState(0); // Permanent record of the user's role_id from API
  const [roles, setRoles] = useState<any[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [userPermissions, setUserPermissions] = useState<any[]>([]); // Store original user permissions
  const [rolePermissions, setRolePermissions] = useState<any[]>([]);
  const [allPermissions, setAllPermissions] = useState<any[]>([]); // Store all available permissions with IDs
  const [isLoadingRolePermissions, setIsLoadingRolePermissions] = useState(false);
  const [isLoadingUserPermissions, setIsLoadingUserPermissions] = useState(false);
  const [selectAll, setSelectAll] = useState(false); // State for select all checkbox
  const { callGetPermissionsMatrix, callGetPermissions } = usePermissionsApi();
  const { callGetRoles, callGetRolePermissions } = useRolesApi();
  const { callGetUserPermissions, callUpdateRolePermissions } = useUsersManagementApi();
  const toast = useToaster();
  const { memberId } = useParams<any>();
  const history = useHistory();

  const handlePermissionChange = (module: string, field: any) => {
    const updatedPermissions = [...permissions];
    if (field !== "type") {
      // Find the permission by module type
      const permissionIndex = updatedPermissions.findIndex((p) => p.type === module);
      if (permissionIndex !== -1) {
        updatedPermissions[permissionIndex][field] = !updatedPermissions[permissionIndex][field];
        setPermissions(updatedPermissions);
      }
    }
  };

  // Handle select all checkbox
  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    const updatedPermissions = permissions.map((permission) => {
      const moduleKey = getModuleKey(permission.type);
      const moduleAvailableActions = availableModuleActions[moduleKey] || [];
      const updatedPermission = { ...permission };

      // Update all available actions for this module
      Object.keys(actionsData).forEach((actionKey) => {
        if (moduleAvailableActions.includes(actionKey)) {
          updatedPermission[actionKey] = checked;
        }
      });

      return updatedPermission;
    });

    setPermissions(updatedPermissions);
  };

  // Check if all available permissions are selected
  const areAllPermissionsSelected = () => {
    if (permissions.length === 0) return false;

    return permissions.every((permission) => {
      const moduleKey = getModuleKey(permission.type);
      const moduleAvailableActions = availableModuleActions[moduleKey] || [];

      return moduleAvailableActions.every((actionKey) => {
        return permission[actionKey] === true;
      });
    });
  };

  // Update selectAll state when permissions change
  useEffect(() => {
    setSelectAll(areAllPermissionsSelected());
  }, [permissions, availableModuleActions]);

  const handleSave = () => {
    if (!memberId) {
      toast.error(t("members.permissions.messages.memberIdRequired"));
      return;
    }

    // Get all checked permissions and their IDs
    const checkedPermissions: number[] = [];

    permissions.forEach((permission) => {
      const moduleKey = getModuleKey(permission.type);

      // Find the original permission data to get IDs
      const originalUserPerm = userPermissions.find((p) => p.type === permission.type);

      // Check each available action type and find corresponding permission ID
      const moduleAvailableActions = availableModuleActions[moduleKey] || [];
      Object.keys(actionsData).forEach((action) => {
        // Only process action if it's available for this module and is checked
        if (moduleAvailableActions.includes(action) && permission[action]) {
          // First try to get ID from stored user permissions
          if (originalUserPerm?.permissionIds?.[action]) {
            checkedPermissions.push(originalUserPerm.permissionIds[action]);
          } else {
            // Fallback: find in allPermissions
            const permissionEntry = allPermissions.find((p) => p.module === moduleKey && p.action === action);
            if (permissionEntry && permissionEntry.id) {
              checkedPermissions.push(permissionEntry.id);
            }
          }
        }
      });
    });

    const payload = {
      role_id: selectedRoleId,
      permissions: checkedPermissions.map((id) => ({ permission_id: id })),
    };

    console.log("Saving permissions with payload:", payload);

    callUpdateRolePermissions(
      memberId,
      payload,
      (message: string, response: any) => {
        toast.success(message || t("members.permissions.messages.saveSuccess"));
        console.log("Permissions saved successfully:", response);

        // Optionally navigate back to members list
        history.push(URLS.MEMBERS.LIST);
      },
      (error: string) => {
        console.error("Error saving permissions:", error);
        toast.error(error || t("members.permissions.messages.saveError"));
      },
    );
  };

  const handleCancel = () => {
    setPermissions([]);
    history.push(URLS.MEMBERS.LIST);
  };

  // Create default permissions structure based on modules from API
  const createDefaultPermissions = () => {
    if (Object.keys(modulesData).length === 0) {
      return [];
    }

    return Object.entries(modulesData).map(([moduleKey, moduleName]) => {
      const permissionObj: any = {
        type: moduleName,
      };

      // Initialize only available actions to false (based on permission status)
      const moduleAvailableActions = availableModuleActions[moduleKey] || [];
      Object.keys(actionsData).forEach((actionKey) => {
        // Only add action if it's available for this module
        if (moduleAvailableActions.includes(actionKey)) {
          permissionObj[actionKey] = false;
        }
      });

      return permissionObj;
    });
  };
  const loadAllPermissions = () => {
    callGetPermissions(
      {},
      (message: string, data: any) => {
        console.log("All permissions loaded successfully:", message, data);
        if (data?.permissions && Array.isArray(data.permissions)) {
          setAllPermissions(data.permissions);
        }

        // Extract modules and actions from the API response
        if (data?.modules) {
          setModulesData(data.modules);
        }

        if (data?.actions) {
          setActionsData(data.actions);
        }

        // Extract available actions per module based on permission status
        if (data?.permissions) {
          const availableModuleActions: { [module: string]: string[] } = {};

          data.permissions.forEach((permission: any) => {
            if (permission.status === true) {
              if (!availableModuleActions[permission.module]) {
                availableModuleActions[permission.module] = [];
              }
              if (!availableModuleActions[permission.module].includes(permission.action)) {
                availableModuleActions[permission.module].push(permission.action);
              }
            }
          });

          setAvailableModuleActions(availableModuleActions);
        }
      },
      (error: string) => {
        console.error("Error loading all permissions:", error);
        // Continue without permissions list - handleSave will handle this gracefully
      },
    );
  };
  const loadRoles = () => {
    setIsLoadingRoles(true);
    callGetRoles(
      {},
      (message: string, data: any) => {
        console.log("Roles loaded successfully:", message);
        if (data?.roles) {
          setRoles(data.roles);
        }
        setIsLoadingRoles(false);
      },
      (error: string) => {
        console.error("Error loading roles:", error);
        toast.error(t("members.permissions.messages.loadRolesError"));
        setIsLoadingRoles(false);
      },
    );
  };

  const fetchRolePermissions = (roleId: number | string) => {
    if (!roleId || roleId === 0) {
      // When no role is selected, restore user permissions and clear role permissions
      setRolePermissions([]);
      setPermissions([...userPermissions]); // Restore original user permissions
      return;
    }

    setIsLoadingRolePermissions(true);
    callGetRolePermissions(
      roleId,
      (message: string, data: any) => {
        console.log("Role permissions loaded successfully:", message, data);

        // Transform the API response to match the permissions format
        if (data?.permissions_by_module) {
          const transformedRolePermissions = Object.keys(data.permissions_by_module).map((moduleKey) => {
            const modulePermissions = data.permissions_by_module[moduleKey];

            // Initialize permission object with module type
            const permissionObj: any = {
              type: getModuleDisplayName(moduleKey),
            };

            // Initialize only available actions to false (based on permission status)
            const moduleAvailableActions = availableModuleActions[moduleKey] || [];
            Object.keys(actionsData).forEach((actionKey) => {
              // Only add action if it's available for this module
              if (moduleAvailableActions.includes(actionKey)) {
                permissionObj[actionKey] = false;
              }
            });

            // Process each permission in the module
            modulePermissions.forEach((permission: any) => {
              if (actionsData[permission.action] && moduleAvailableActions.includes(permission.action)) {
                permissionObj[permission.action] = permission.status === true;
              }
            });

            return permissionObj;
          });

          console.log("Transformed role permissions:", transformedRolePermissions);
          setRolePermissions(transformedRolePermissions);

          // If the selected role matches the user's own role_id from the API,
          // use user permissions as-is (don't override with role permissions)
          const fetchedRoleId = typeof roleId === "string" ? parseInt(roleId) : roleId;
          if (fetchedRoleId === userRoleId) {
            console.log("Role matches user's own role — using user permissions as-is");
            setPermissions([...userPermissions]);
          } else {
            // Different role selected — merge role permissions with user permissions
            // Role permissions take precedence (they will be enabled and disabled)
            // User permissions that are not in role remain editable
            const mergedPermissions = userPermissions.map((userPerm) => {
              const rolePerm = transformedRolePermissions.find((rp) => rp.type === userPerm.type);
              if (rolePerm) {
                // For each available action, use role permission if it exists, otherwise keep user permission
                const mergedPerm: any = {
                  type: userPerm.type,
                };

                const moduleKey = getModuleKey(userPerm.type);
                const moduleAvailableActions = availableModuleActions[moduleKey] || [];

                Object.keys(actionsData).forEach((actionKey) => {
                  // Only process action if it's available for this module
                  if (moduleAvailableActions.includes(actionKey)) {
                    mergedPerm[actionKey] = rolePerm[actionKey] || userPerm[actionKey];
                  }
                });

                return mergedPerm;
              }
              return { ...userPerm };
            });
            setPermissions(mergedPermissions);
          }
        } else {
          const defaultPerms = createDefaultPermissions();
          setRolePermissions(defaultPerms);
          setPermissions([...userPermissions]); // Keep user permissions if no role permissions
        }
        setIsLoadingRolePermissions(false);
      },
      (error: string) => {
        console.error("Error loading role permissions:", error);
        toast.error(t("members.permissions.messages.loadRolePermissionsError"));
        const defaultPerms = createDefaultPermissions();
        setRolePermissions(defaultPerms);
        setPermissions([...userPermissions]); // Keep user permissions on error
        setIsLoadingRolePermissions(false);
      },
    );
  };

  const getUserPermissions = () => {
    if (!memberId) {
      console.warn("No memberId provided");
      return;
    }

    setIsLoadingUserPermissions(true);
    callGetUserPermissions(
      memberId,
      (message: string, data: any) => {
        console.log("User permissions loaded successfully:", message, data);

        // Transform the API response to match the permissions format
        if (data?.permissions && Array.isArray(data.permissions)) {
          // Group permissions by module
          const permissionsByModule: { [key: string]: any } = {};

          data.permissions.forEach((permission: any) => {
            const moduleName = permission.module;
            const action = permission.action;
            const hasPermission = permission.status === true;
            const permissionId = permission.permission_id;

            if (!permissionsByModule[moduleName]) {
              permissionsByModule[moduleName] = {
                type: getModuleDisplayName(moduleName),
                // Store permission IDs for each action
                permissionIds: {},
              };

              // Initialize only available actions to false (based on permission status)
              const moduleAvailableActions = availableModuleActions[moduleName] || [];
              Object.keys(actionsData).forEach((actionKey) => {
                // Only add action if it's available for this module
                if (moduleAvailableActions.includes(actionKey)) {
                  permissionsByModule[moduleName][actionKey] = false;
                  permissionsByModule[moduleName].permissionIds[actionKey] = null;
                }
              });
            }

            // Map action to the appropriate permission if it exists in actionsData and is available for this module
            const moduleAvailableActions = availableModuleActions[moduleName] || [];
            if (actionsData[action] && moduleAvailableActions.includes(action)) {
              permissionsByModule[moduleName][action] = hasPermission;
              permissionsByModule[moduleName].permissionIds[action] = permissionId;
            }
          }); // Convert to array format and ensure all dynamic modules are present
          const defaultPerms = createDefaultPermissions();
          const transformedPermissions = defaultPerms.map((defaultPerm) => {
            const moduleKey = getModuleKey(defaultPerm.type);
            return (
              permissionsByModule[moduleKey] || {
                ...defaultPerm,
                permissionIds: {
                  create: null,
                  read: null,
                  edit: null,
                  delete: null,
                },
              }
            );
          });

          console.log("Transformed user permissions:", transformedPermissions);

          // Store the original user permissions for later restoration
          setUserPermissions([...transformedPermissions]);
          setPermissions([...transformedPermissions]);

          // Update member info and role if available
          if (data.member_id) {
            console.log(`Loaded permissions for member: ${data.member_name} (${data.member_email})`);
          }

          // Set the role dropdown if role_id is provided in the response
          if (data.role_id) {
            const roleId = parseInt(data.role_id);
            setSelectedRoleId(roleId);
            setSelectedRole(data.role_name || "");
            setInitialRoleId(roleId); // Store for later use
            setUserRoleId(roleId); // Permanent record of the user's role_id
            console.log(`Setting initial role: ${data.role_name} (ID: ${roleId})`);
          }
        } else {
          const defaultPerms = createDefaultPermissions().map((perm) => {
            const permWithIds = { ...perm };
            permWithIds.permissionIds = {};

            // Initialize permission IDs only for available actions
            const moduleKey = getModuleKey(perm.type);
            const moduleAvailableActions = availableModuleActions[moduleKey] || [];
            Object.keys(actionsData).forEach((actionKey) => {
              // Only add permission ID if action is available for this module
              if (moduleAvailableActions.includes(actionKey)) {
                permWithIds.permissionIds[actionKey] = null;
              }
            });

            return permWithIds;
          });
          setUserPermissions(defaultPerms);
          setPermissions(defaultPerms);
        }
        setIsLoadingUserPermissions(false);
      },
      (error: string) => {
        console.error("Error loading user permissions:", error);
        toast.error(t("members.permissions.messages.loadUserPermissionsError"));
        const defaultPerms = createDefaultPermissions().map((perm) => {
          const permWithIds = { ...perm };
          permWithIds.permissionIds = {};

          // Initialize permission IDs only for available actions
          const moduleKey = getModuleKey(perm.type);
          const moduleAvailableActions = availableModuleActions[moduleKey] || [];
          Object.keys(actionsData).forEach((actionKey) => {
            // Only add permission ID if action is available for this module
            if (moduleAvailableActions.includes(actionKey)) {
              permWithIds.permissionIds[actionKey] = null;
            }
          });

          return permWithIds;
        });
        setUserPermissions(defaultPerms);
        setPermissions(defaultPerms);
        setIsLoadingUserPermissions(false);
      },
    );
  };

  useEffect(() => {
    loadAllPermissions();
    loadRoles();
  }, []);

  useEffect(() => {
    // Load user permissions when memberId changes and both roles, modules, and available actions are loaded
    if (
      memberId &&
      roles.length > 0 &&
      Object.keys(modulesData).length > 0 &&
      Object.keys(availableModuleActions).length > 0
    ) {
      getUserPermissions();
    }
  }, [memberId, roles, modulesData, availableModuleActions]);

  // Handle loading initial role permissions after user permissions are loaded
  useEffect(() => {
    if (initialRoleId && initialRoleId !== 0 && userPermissions.length > 0) {
      console.log(`Loading initial role permissions for role ID: ${initialRoleId}`);
      fetchRolePermissions(initialRoleId);
      setInitialRoleId(0); // Reset to prevent multiple calls
    }
  }, [initialRoleId, userPermissions]);

  return (
    <div className="drjob-permission-list">
      {/* Role Selection */}
      <div className="drjob-role-section">
        <h3>{t("members.permissions.title")}</h3>
        <div className="drjob-role-select">
          <select
            value={selectedRoleId}
            onChange={(e) => {
              const roleId = parseInt(e.target.value);
              const role = roles.find((r) => r.id === roleId);
              setSelectedRoleId(roleId);
              setSelectedRole(role?.name || "");

              // Fetch permissions for the selected role or restore user permissions
              fetchRolePermissions(roleId);
            }}
            className={`form-control w-100 ${isLoadingRoles ? "loading" : ""}`}
            disabled={isLoadingRoles || isLoadingRolePermissions || isLoadingUserPermissions}
          >
            {/* <option value={0}>{isLoadingRoles ? "Loading roles..." : "Select Role"}</option> */}
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
          <i className="fa fa-chevron-down drjob-select-arrow"></i>
        </div>
      </div>

      {/* Select All Checkbox */}
      {!isLoadingRolePermissions &&
        !isLoadingUserPermissions &&
        Object.keys(modulesData).length > 0 &&
        Object.keys(actionsData).length > 0 &&
        Object.keys(availableModuleActions).length > 0 && (
          <div className="drjob-select-all-container">
            <label className="drjob-select-all-label">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="drjob-select-all-checkbox"
              />
              <span className="drjob-select-all-text">{t("members.permissions.selectAll")}</span>
            </label>
          </div>
        )}

      {/* Permission Table */}
      <div className="drjob-permission-table-container">
        {isLoadingRolePermissions || isLoadingUserPermissions ? (
          <div className="loading-message" style={{ textAlign: "center", padding: "20px" }}>
            {isLoadingUserPermissions
              ? t("members.permissions.loading.userPermissions")
              : t("members.permissions.loading.rolePermissions")}
          </div>
        ) : Object.keys(modulesData).length === 0 ||
          Object.keys(actionsData).length === 0 ||
          Object.keys(availableModuleActions).length === 0 ? (
          <div className="loading-message" style={{ textAlign: "center", padding: "20px" }}>
            Loading permissions structure...
          </div>
        ) : (
          <table className="drjob-permission-table">
            <thead>
              <tr>
                <th className="drjob-type-header">
                  {t("members.permissions.table.headers.type")}
                  {/* <i className="fa fa-sort"></i> */}
                </th>
                {Object.entries(actionsData).map(([actionKey, actionName]) => (
                  <th key={actionKey}>{actionName}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {permissions.map((permission, index) => {
                const moduleKey = getModuleKey(permission.type);
                const moduleAvailableActions = availableModuleActions[moduleKey] || [];

                return (
                  <tr key={index}>
                    <td className="drjob-permission-type">{permission.type}</td>
                    {Object.keys(actionsData).map((actionKey) => (
                      <td key={actionKey}>
                        {moduleAvailableActions.includes(actionKey) ? (
                          <label className="drjob-checkbox-container">
                            <input
                              type="checkbox"
                              checked={permission[actionKey] || false}
                              onChange={() => handlePermissionChange(permission.type, actionKey)}
                            />
                            <span className="drjob-checkmark"></span>
                          </label>
                        ) : (
                          <label className="drjob-checkbox-container">
                            <input type="checkbox" checked={false} disabled />
                            <span className="drjob-checkmark"></span>
                          </label>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Action Buttons */}
      <div className="drjob-permission-actions">
        <button type="button" className="btn-cancel" onClick={handleCancel}>
          {t("members.permissions.buttons.cancel")}
        </button>
        <button type="button" className="btn-preview" onClick={handleSave}>
          {t("members.permissions.buttons.save")}
        </button>
      </div>
    </div>
  );
};

export default PermissionList;
